/**
 * Leave entitlement maths for a person's Time off tab: what they are owed, what
 * they have accrued so far, what they have taken, and what is left.
 *
 * Separate from `pto.ts`, which is the company report's rollups. This is the
 * per-person balance question, and it is the only thing that has to reconcile
 * NOS's two different units — entitlement is in DAYS (`PersonPeriodPTO`) while
 * leave taken is in HOURS (`TimeEntry`). See `docs/nos-gateway-endpoints.md`.
 */

import { HOURS_PER_DAY, type PtoEntry, type PtoRequest, type PtoType } from "@/lib/pto";

/**
 * The two buckets the tab shows. Observed Paid Holiday is excluded on purpose —
 * it is a company closure rather than personal leave, so it has an allowance but
 * does not belong in a balance someone reads as "my time off". Unpaid and
 * Exceptional have no allowance in NOS at all.
 */
export type PtoBucketKey = "pto" | "sick";

/** Which leave type backs each bucket. */
const BUCKET_TYPE: Record<PtoBucketKey, PtoType> = {
  pto: "General PTO",
  sick: "Sick PTO",
};

export const BUCKET_LABELS: Record<PtoBucketKey, string> = {
  pto: "PTO",
  sick: "Sick",
};

export const PTO_BUCKETS: readonly PtoBucketKey[] = ["pto", "sick"];

/**
 * How NOS grants a type's allowance.
 *
 * `quarter` is General PTO — four rows a year, no annual row, so the year is
 * their sum and the accrual is real. `year` is Sick and Observed Holiday — one
 * lump grant, where "earned to date" is the whole allowance and printing it as
 * a separate figure would imply a distinction that does not exist.
 */
export type AllowanceGrain = "quarter" | "year";

/** One allowance row: a type's days for one period. */
export interface PtoAllowancePeriod {
  type: PtoType;
  grain: AllowanceGrain;
  /** NOS's period name, e.g. `Q3 2026` — shown so the accrual rule is legible. */
  label: string;
  days: number;
  /** Date-only ISO bounds of the period. */
  start: string;
  end: string;
}

/** One bucket's figures. Every day count is in DAYS; nulls mean "not recorded". */
export interface PtoBucketSummary {
  key: PtoBucketKey;
  /** The year's full entitlement, or null when NOS records none for this person. */
  allowanceDays: number | null;
  /**
   * Earned so far, for a type that accrues. Null for an annual lump grant (where
   * it would just repeat `allowanceDays`) and null when nothing is recorded.
   */
  accruedDays: number | null;
  /** Which periods `accruedDays` counted, e.g. `Q1 2026–Q3 2026`. */
  accruedLabel: string | null;
  /** Approved leave taken this year. Always known, even with no allowance. */
  usedDays: number;
  /** Days sitting in unapproved requests. Shown beside the balance, never in it. */
  pendingDays: number;
  /**
   * Allowance minus approved — what is still bookable this year. Deliberately
   * NOT minus pending: an unapproved request must not move the balance, and a
   * rejected one would otherwise silently hand days back.
   *
   * Can go negative, and is left negative: someone who has over-booked needs to
   * see that, and clamping at zero would hide it.
   */
  remainingDays: number | null;
}

/**
 * Rounds to two decimals so summing eighths of a day does not surface as
 * `21.499999999999996`. Two is enough: NOS's own allowances go to 0.05 of a day.
 */
function round(days: number): number {
  return Number(days.toFixed(2));
}

/**
 * Which allowance periods have begun by `today`.
 *
 * A period counts in full from its FIRST day — NOS grants a quarter's days as a
 * lump, so there is nothing to pro-rate within one. That makes the figure step
 * up on the first of January, April, July and October.
 */
function startedPeriods(periods: PtoAllowancePeriod[], today: string): PtoAllowancePeriod[] {
  // Date-only ISO strings compare correctly as strings.
  return periods.filter((p) => p.start <= today);
}

/**
 * One bucket's summary from its allowance rows, the year's approved leave, and
 * its outstanding requests.
 *
 * `entries` and `requests` may cover every leave type; this filters to the
 * bucket's own, so callers pass the whole year without pre-splitting it.
 */
export function summariseBucket(
  key: PtoBucketKey,
  allowances: PtoAllowancePeriod[],
  entries: PtoEntry[],
  requests: PtoRequest[],
  today: string,
): PtoBucketSummary {
  const type = BUCKET_TYPE[key];
  const mine = allowances.filter((a) => a.type === type);

  const usedHours = entries.filter((e) => e.type === type).reduce((sum, e) => sum + e.hours, 0);
  const usedDays = round(usedHours / HOURS_PER_DAY);

  const pendingDays = round(
    requests.filter((r) => r.type === type).reduce((sum, r) => sum + r.days, 0),
  );

  if (mine.length === 0) {
    // No entitlement recorded. Used and pending are still real and still shown;
    // everything derived from an allowance stays null so the UI can say "not
    // recorded" rather than print a zero that reads as "no leave left".
    return {
      key,
      allowanceDays: null,
      accruedDays: null,
      accruedLabel: null,
      usedDays,
      pendingDays,
      remainingDays: null,
    };
  }

  const allowanceDays = round(mine.reduce((sum, a) => sum + a.days, 0));
  const accrues = mine.some((a) => a.grain === "quarter");
  const started = accrues ? startedPeriods(mine, today) : [];
  const first = started[0];
  const last = started.at(-1);

  return {
    key,
    allowanceDays,
    accruedDays: accrues ? round(started.reduce((sum, a) => sum + a.days, 0)) : null,
    accruedLabel:
      first === undefined || last === undefined
        ? null
        : first.label === last.label
          ? first.label
          : `${first.label}–${last.label}`,
    usedDays,
    pendingDays,
    remainingDays: round(allowanceDays - usedDays),
  };
}

/** `22.5` → `"22.5"`, `1` → `"1"`. Trimmed, so whole days read as whole days. */
export function formatDayCount(days: number): string {
  return String(Number(days.toFixed(2)));
}
